import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  HomeIcon, SettingsIcon, KeyIcon, BarChartIcon,
  CloudIcon, UsersIcon, ZapIcon, LayersIcon,
  PlayIcon, ActivityIcon, LogOutIcon, ServerIcon,
  FileTextIcon, PuzzleIcon, NetworkIcon, BrainIcon,
  DatabaseIcon, KeyRoundIcon, CodeIcon, CreditCardIcon,
  DollarSignIcon, FlaskConicalIcon, PiggyBankIcon,
  SearchIcon, ShieldIcon, BellIcon, Building2Icon,
  ClipboardListIcon, GaugeIcon
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const Sidebar = ({ open, setOpen }: SidebarProps) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const platformNavigation = [
    { name: "Dashboard", href: "/", icon: HomeIcon },
    { name: "Playground", href: "/playground", icon: PlayIcon },
    { name: "Providers", href: "/providers", icon: CloudIcon },
    { name: "Model Subscriptions", href: "/model-subscriptions", icon: DatabaseIcon },
    { name: "API Keys", href: "/api-keys", icon: KeyIcon },
    { name: "Provider API Keys", href: "/provider-api-keys", icon: KeyRoundIcon },
    { name: "API Integration", href: "/api-integration", icon: CodeIcon },
    { name: "Integrations", href: "/integrations", icon: LayersIcon },
  ];
  
  const aiFeatureNavigation = [
    { name: "LangChain", href: "/langchain", icon: ZapIcon },
    { name: "LangGraph", href: "/langgraph", icon: NetworkIcon },
    { name: "Agents", href: "/agents", icon: BrainIcon },
    { name: "RAG", href: "/rag", icon: DatabaseIcon },
  ];
  
  const dataNavigation = [
    { name: "Datasets", href: "/datasets", icon: FileTextIcon },
    { name: "Fine-Tuning", href: "/fine-tuning", icon: PuzzleIcon },
  ];
  
  const operationsNavigation = [
    { name: "Tracing", href: "/tracing", icon: SearchIcon },
    { name: "Guardrails", href: "/guardrails", icon: ShieldIcon },
  ];

  const analyticsNavigation = [
    { name: "Analytics", href: "/analytics", icon: BarChartIcon },
    { name: "Consumption", href: "/consumption", icon: DollarSignIcon },
    { name: "Usage", href: "/usage", icon: ActivityIcon },
    { name: "Billing", href: "/billing", icon: CreditCardIcon },
    { name: "A/B Testing", href: "/ab-testing", icon: FlaskConicalIcon },
    { name: "Cost Optimization", href: "/cost-optimization", icon: PiggyBankIcon },
  ];

  // User account section (available to all users)
  const accountNavigation = [
    { name: "Notifications", href: "/notifications", icon: BellIcon },
    { name: "Organizations", href: "/organizations", icon: Building2Icon },
    { name: "Settings", href: "/settings", icon: SettingsIcon },
  ];

  // Admin-only section
  const adminNavigation = [
    { name: "Team Management", href: "/team", icon: UsersIcon },
    { name: "Invitations", href: "/invitations", icon: UsersIcon },
    { name: "Audit Logs", href: "/admin/audit-logs", icon: ClipboardListIcon },
    { name: "SLA Dashboard", href: "/admin/sla", icon: GaugeIcon },
    { name: "Provider Configs", href: "/admin/provider-configs", icon: ServerIcon },
    { name: "System Settings", href: "/admin/settings", icon: ServerIcon },
  ];

  const isActive = (href: string) => location.pathname === href;
  const isAdmin = user?.role === "admin";

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.username) return "U";
    return user.username.split(" ")
      .map(part => part.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div
      className={cn(
        "fixed top-16 left-0 h-[calc(100vh-4rem)] z-[100] bg-neutral-50 dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 transition-all duration-300",
        open ? "w-64" : "w-20"
      )}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-grow overflow-y-auto pt-4 pb-[60px]">
          {/* Platform Section */}
          <div className="px-4 py-6">
            {open && (
              <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                Platform
              </h3>
            )}
            <div className="flex flex-col gap-1.5">
              {platformNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                    isActive(item.href) 
                      ? "bg-primary/10 text-primary font-medium" 
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                  )} />
                  {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                  {!open && (
                    <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                      {item.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
          
          {/* AI Features Section */}
          <div className="px-4 pt-4">
            {open && (
              <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                AI Features
              </h3>
            )}
            <div className="flex flex-col gap-1.5">
              {aiFeatureNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                    isActive(item.href) 
                      ? "bg-primary/10 text-primary font-medium" 
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                  )} />
                  {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                  {!open && (
                    <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                      {item.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Data Section */}
          <div className="px-4 pt-4">
            {open && (
              <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                Data
              </h3>
            )}
            <div className="flex flex-col gap-1.5">
              {dataNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                    isActive(item.href) 
                      ? "bg-primary/10 text-primary font-medium" 
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                  )} />
                  {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                  {!open && (
                    <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                      {item.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Operations Section */}
          <div className="px-4 pt-4">
            {open && (
              <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                Operations
              </h3>
            )}
            <div className="flex flex-col gap-1.5">
              {operationsNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                    isActive(item.href)
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                  )} />
                  {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                  {!open && (
                    <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                      {item.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Analytics Section */}
          <div className="px-4 pt-4">
            {open && (
              <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                Analytics
              </h3>
            )}
            <div className="flex flex-col gap-1.5">
              {analyticsNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                    isActive(item.href)
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                  )} />
                  {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                  {!open && (
                    <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                      {item.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Account Section - Available to all users */}
          <div className="px-4 pt-4">
            {open && (
              <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                Account
              </h3>
            )}
            <div className="flex flex-col gap-1.5">
              {accountNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                    isActive(item.href)
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                  )} />
                  {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                  {!open && (
                    <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                      {item.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Admin Section - Only visible to admins */}
          {isAdmin && (
            <div className="px-4 pt-4">
              {open && (
                <h3 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3 pl-3">
                  Admin
                </h3>
              )}
              <div className="flex flex-col gap-1.5">
                {adminNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md group transition-colors",
                      isActive(item.href) 
                        ? "bg-primary/10 text-primary font-medium" 
                        : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    )}
                  >
                    <item.icon className={cn(
                      "h-5 w-5 flex-shrink-0",
                      isActive(item.href) ? "text-primary" : "text-neutral-400 dark:text-neutral-500"
                    )} />
                    {open && <span className="font-space text-sm whitespace-nowrap">{item.name}</span>}
                    {!open && (
                      <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                        {item.name}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* User and logout section - Fixed at bottom */}
        <div className="mt-auto border-t border-neutral-200 dark:border-neutral-800 p-4 bg-neutral-50 dark:bg-neutral-900">
          {open ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 px-2">
                <Avatar className="h-9 w-9">
                  <AvatarFallback>{getUserInitials()}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="font-space text-sm font-medium">{user?.username || 'User'}</span>
                  <span className="font-space text-xs text-neutral-500">{user?.email}</span>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full font-space gap-2 justify-start text-sm"
                onClick={handleLogout}
              >
                <LogOutIcon className="h-4 w-4" /> Sign Out
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <Avatar className="h-9 w-9">
                <AvatarFallback>{getUserInitials()}</AvatarFallback>
              </Avatar>
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleLogout}
                className="group relative"
              >
                <LogOutIcon className="h-4 w-4" />
                <span className="fixed left-20 scale-0 rounded bg-neutral-800 px-2 py-1 text-xs font-medium text-white group-hover:scale-100 z-50">
                  Sign Out
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
